import { useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { motion } from "framer-motion";
import { Star, ShoppingBag, Sparkles, Pill, FlaskConical, Leaf } from "lucide-react";

function Price({ price, discount }) {
  const hasDiscount = typeof discount === "number" && discount > 0 && discount < price;
  return (
    <div className="flex items-baseline gap-2">
      <span className="font-mono text-base font-semibold text-zinc-900">
        {(hasDiscount ? discount : price).toFixed(2)} €
      </span>
      {hasDiscount && (
        <span className="font-mono text-[11px] text-zinc-400 line-through">
          {price.toFixed(2)} €
        </span>
      )}
    </div>
  );
}

function CategoryIcon({ category, className = "h-14 w-14" }) {
  if (category === "vitamins") return <Leaf className={className} />;
  if (category === "minerals") return <FlaskConical className={className} />;
  return <Pill className={className} />;
}

function ProductImage({ s }) {
  const [errored, setErrored] = useState(false);
  const showImg = s.image_url && !errored;

  return (
    <div className="relative aspect-square w-full overflow-hidden">
      {/* Gradient base */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(120% 80% at 30% 20%, rgba(196,192,245,0.55), rgba(81,75,186,0.05) 65%, white 100%)",
        }}
      />
      {/* Dotted ring decoration */}
      <svg
        className="absolute inset-0 h-full w-full"
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
      >
        <circle cx="50" cy="50" r="36" fill="none" stroke="#514bba" strokeOpacity="0.18" strokeDasharray="1 3" />
        <circle cx="50" cy="50" r="26" fill="none" stroke="#514bba" strokeOpacity="0.1" />
      </svg>

      {/* Fallback icon (always rendered, hidden when image loaded) */}
      {!showImg && (
        <div className="absolute inset-0 flex items-center justify-center text-[color:var(--sentinel)]">
          <CategoryIcon category={s.category} className="h-16 w-16" strokeWidth={1.4} />
        </div>
      )}

      {/* Image overlay */}
      {showImg && (
        <img
          src={s.image_url}
          alt={s.name}
          loading="lazy"
          referrerPolicy="no-referrer"
          className="relative h-full w-full object-contain p-6 transition-transform duration-500 group-hover:scale-105"
          onError={() => setErrored(true)}
        />
      )}

      {s.popular && (
        <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-zinc-900 px-2.5 py-1 text-[10px] font-semibold text-white">
          <Sparkles className="h-3 w-3 text-[color:var(--sentinel-accent)]" />
          Popular
        </span>
      )}
    </div>
  );
}

function Card({ s, reason, idx }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.45, delay: Math.min(idx * 0.05, 0.4) }}
      className="group flex flex-col overflow-hidden rounded-3xl border border-zinc-200 bg-white transition hover:border-[color:var(--sentinel-accent)] hover:shadow-[0_24px_50px_-30px_rgba(81,75,186,0.4)]"
      data-testid={`supplement-card-${s.uid}`}
    >
      <ProductImage s={s} />
      <div className="flex flex-1 flex-col p-5">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-zinc-400">
          {s.category}
          {s.brand ? ` · ${s.brand}` : ""}
        </p>
        <h3 className="mt-2 line-clamp-2 text-base font-semibold leading-snug text-zinc-900">
          {s.name}
        </h3>
        <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-zinc-500">
          {s.description}
        </p>
        {reason && (
          <p className="mt-3 inline-flex items-start gap-1.5 rounded-lg bg-[color:var(--sentinel-tint)] px-2.5 py-1.5 text-[11px] font-medium text-[color:var(--sentinel-darker)]">
            <Sparkles className="mt-0.5 h-3 w-3 shrink-0" />
            <span>{reason}</span>
          </p>
        )}
        <div className="mt-4 flex items-end justify-between border-t border-dashed border-zinc-200 pt-4">
          <Price price={s.price} discount={s.discount_price} />
          {s.rating ? (
            <span className="inline-flex items-center gap-1 font-mono text-xs text-zinc-500">
              <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
              {s.rating}
            </span>
          ) : null}
        </div>
      </div>
    </motion.article>
  );
}

export default function SupplementsSection({ recommendations, onUnlockMore, embedded = false }) {
  const { t } = useLang();
  const { topPicks } = recommendations;
  if (!topPicks || topPicks.length === 0) return null;

  return (
    <section
      id={embedded ? undefined : "supplements"}
      data-testid="supplements-section"
      className={embedded ? "relative" : "relative scroll-mt-24 py-20 md:py-24"}
    >
      {!embedded && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -z-10"
          style={{
            backgroundImage:
              "radial-gradient(600px 300px at 90% 0%, rgba(196,192,245,0.18), transparent), radial-gradient(600px 300px at 0% 100%, rgba(81,75,186,0.08), transparent)",
          }}
        />
      )}
      <div className={embedded ? "" : "container-x"}>
        {!embedded && (
          <div className="flex flex-col items-start gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <span className="overline" data-testid="supplements-overline">
                {t.supplements.overline}
              </span>
              <h2 className="mt-3 text-4xl text-zinc-900 md:text-[44px]">
                {t.supplements.title}
              </h2>
              <p className="mt-3 max-w-2xl text-base text-zinc-600">{t.supplements.subtitle}</p>
            </div>
            <span className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-3.5 py-2 text-xs font-medium text-zinc-600">
              <Pill className="h-3.5 w-3.5 text-[color:var(--sentinel)]" />
              {t.supplements.curated}
            </span>
          </div>
        )}

        <div className={embedded ? "grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" : "mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3"}>
          {topPicks.map((s, i) => (
            <Card
              key={s.uid}
              s={s}
              idx={i}
              reason={s.reasonKey ? t.supplements.reasons[s.reasonKey.split(".").pop()] : null}
            />
          ))}
        </div>

        <div className="mt-10 flex flex-col items-center gap-4 rounded-3xl border border-zinc-200 bg-white p-7 md:flex-row md:justify-between">
          <div className="max-w-xl">
            <h3 className="text-xl text-zinc-900 md:text-2xl">
              {t.supplements.unlockTitle}
            </h3>
            <p className="mt-1.5 text-sm text-zinc-600">{t.supplements.unlockSub}</p>
          </div>
          <button
            onClick={onUnlockMore}
            className="btn-primary"
            data-testid="supplements-unlock-cta"
          >
            <ShoppingBag className="h-4 w-4" />
            {t.supplements.unlockCta}
          </button>
        </div>

        <p className="mt-5 text-center text-[11px] text-zinc-400 md:text-left">
          {t.supplements.disclaimer}
        </p>
      </div>
    </section>
  );
}
